/**
 * notificationService.js - Browser Push Notification Service
 * Handles permission requests, subscription management, and local notifications
 */

const NOTIFICATION_TYPES = {
  SUPPORT_TICKET: 'support_ticket',
  VOICEMAIL: 'voicemail',
  INCOMING_CALL: 'incoming_call',
  MESSAGE: 'message',
  BOOKING: 'booking',
  JOB_ASSIGNMENT: 'job_assignment',
  // Ride notifications
  RIDE_DRIVER_ACCEPTED: 'ride_driver_accepted',
  RIDE_DRIVER_EN_ROUTE: 'ride_driver_en_route',
  RIDE_DRIVER_ARRIVED: 'ride_driver_arrived',
  RIDE_STARTED: 'ride_started',
  RIDE_COMPLETED: 'ride_completed',
  RIDE_CANCELLED: 'ride_cancelled'
};

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.isSupported = 'Notification' in window;
    this.serviceWorkerRegistration = null;
    
    if (this.isSupported) {
      this.permission = Notification.permission;
    }
  }

  /**
   * Check if notifications are supported
   */
  isAvailable() {
    return this.isSupported;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Notifications not supported');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    if (this.permission === 'denied') {
      console.warn('Notification permission previously denied');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      this.permission = result;
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Register service worker for push notifications
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return null;
    }

    try {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/service-worker.js');
      return this.serviceWorkerRegistration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  }

  /**
   * Show a local notification
   */
  async showNotification(title, options = {}) {
    if (!this.isSupported || this.permission !== 'granted') {
      console.warn('Cannot show notification - permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-72.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      ...options
    };

    try {
      // Use service worker if available for better reliability
      if (this.serviceWorkerRegistration) {
        return await this.serviceWorkerRegistration.showNotification(title, defaultOptions);
      }
      
      // Fallback to regular Notification
      return new Notification(title, defaultOptions);
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show notification for new support ticket
   */
  showSupportTicketNotification(ticket) {
    return this.showNotification('New Support Ticket', {
      body: `${ticket.user_name}: ${ticket.subject}`,
      tag: `ticket-${ticket.id}`,
      data: { type: NOTIFICATION_TYPES.SUPPORT_TICKET, ticketId: ticket.id },
      requireInteraction: true,
      actions: [
        { action: 'view', title: 'View' },
        { action: 'dismiss', title: 'Dismiss' }
      ]
    });
  }

  /**
   * Show notification for new voicemail
   */
  showVoicemailNotification(voicemail) {
    return this.showNotification('New Voicemail', {
      body: `${voicemail.sender_name} left a ${voicemail.duration_seconds}s voicemail`,
      tag: `voicemail-${voicemail.id}`,
      data: { type: NOTIFICATION_TYPES.VOICEMAIL, voicemailId: voicemail.id },
      requireInteraction: true,
      actions: [
        { action: 'listen', title: 'Listen' },
        { action: 'dismiss', title: 'Dismiss' }
      ]
    });
  }

  /**
   * Show notification for incoming call
   */
  showIncomingCallNotification(caller) {
    return this.showNotification('Incoming Call', {
      body: `${caller.name} is calling you`,
      tag: `call-${caller.callId}`,
      data: { type: NOTIFICATION_TYPES.INCOMING_CALL, callId: caller.callId, callerId: caller.id },
      requireInteraction: true,
      vibrate: [300, 100, 300, 100, 300],
      actions: [
        { action: 'answer', title: 'Answer' },
        { action: 'decline', title: 'Decline' }
      ]
    });
  }

  /**
   * Show notification for new message
   */
  showMessageNotification(message) {
    return this.showNotification('New Message', {
      body: `${message.sender_name}: ${message.content?.substring(0, 100) || 'New message'}`,
      tag: `message-${message.id}`,
      data: { type: NOTIFICATION_TYPES.MESSAGE, messageId: message.id, conversationId: message.conversation_id }
    });
  }

  /**
   * Show notification for booking update
   */
  showBookingNotification(title, booking) {
    return this.showNotification(title, {
      body: `${booking.service_name} - ${new Date(booking.booking_date).toLocaleDateString()}`,
      tag: `booking-${booking.id}`,
      data: { type: NOTIFICATION_TYPES.BOOKING, bookingId: booking.id }
    });
  }

  /**
   * Show notification for job assignment
   */
  showJobAssignmentNotification(job) {
    return this.showNotification('New Job Assigned', {
      body: `${job.service_name} on ${new Date(job.booking_date).toLocaleDateString()}`,
      tag: `job-${job.id}`,
      data: { type: NOTIFICATION_TYPES.JOB_ASSIGNMENT, jobId: job.id },
      requireInteraction: true
    });
  }

  // ============== RIDE NOTIFICATIONS ==============

  /**
   * Show notification when driver accepts ride
   */
  showDriverAcceptedNotification(ride) {
    this.playNotificationSound('success');
    return this.showNotification('Driver Accepted! 🚗', {
      body: `${ride.driver_name || 'Your driver'} is on the way to pick you up`,
      tag: `ride-accepted-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_DRIVER_ACCEPTED, rideId: ride.id },
      requireInteraction: true,
      vibrate: [200, 100, 200, 100, 200]
    });
  }

  /**
   * Show notification when driver is en route to pickup
   */
  showDriverEnRouteNotification(ride) {
    this.playNotificationSound('info');
    return this.showNotification('Driver En Route 🚙', {
      body: `${ride.driver_name || 'Your driver'} is heading to your pickup location`,
      tag: `ride-enroute-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_DRIVER_EN_ROUTE, rideId: ride.id }
    });
  }

  /**
   * Show notification when driver arrives at pickup
   */
  showDriverArrivedNotification(ride) {
    this.playNotificationSound('alert');
    return this.showNotification('Driver Has Arrived! 📍', {
      body: `${ride.driver_name || 'Your driver'} is waiting at the pickup location`,
      tag: `ride-arrived-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_DRIVER_ARRIVED, rideId: ride.id },
      requireInteraction: true,
      vibrate: [300, 100, 300, 100, 300, 100, 300]
    });
  }

  /**
   * Show notification when ride starts
   */
  showRideStartedNotification(ride) {
    this.playNotificationSound('info');
    return this.showNotification('Ride Started 🚀', {
      body: `Your ride to ${ride.dropoff_location?.city || 'destination'} has begun`,
      tag: `ride-started-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_STARTED, rideId: ride.id }
    });
  }

  /**
   * Show notification when ride completes
   */
  showRideCompletedNotification(ride) {
    this.playNotificationSound('success');
    const fareText = ride.final_fare ? `$${ride.final_fare.toFixed(2)}` : '';
    return this.showNotification('Ride Completed! ✅', {
      body: `You've arrived at your destination${fareText ? `. Fare: ${fareText}` : ''}`,
      tag: `ride-completed-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_COMPLETED, rideId: ride.id }
    });
  }

  /**
   * Show notification when ride is cancelled
   */
  showRideCancelledNotification(ride, reason = '') {
    this.playNotificationSound('error');
    return this.showNotification('Ride Cancelled ❌', {
      body: reason || `Ride ${ride.reference || ''} has been cancelled`,
      tag: `ride-cancelled-${ride.id}`,
      data: { type: NOTIFICATION_TYPES.RIDE_CANCELLED, rideId: ride.id }
    });
  }

  // ============== SOUND MANAGEMENT ==============

  /**
   * Play notification sound
   * @param {string} type - 'success', 'error', 'info', 'alert'
   */
  playNotificationSound(type = 'info') {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      // Different sound profiles for different notification types
      switch (type) {
        case 'success':
          // Pleasant ascending tone
          oscillator.frequency.setValueAtTime(523.25, audioCtx.currentTime); // C5
          oscillator.frequency.setValueAtTime(659.25, audioCtx.currentTime + 0.1); // E5
          oscillator.frequency.setValueAtTime(783.99, audioCtx.currentTime + 0.2); // G5
          gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.4);
          break;

        case 'alert':
          // Attention-grabbing double beep
          oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // A5
          gainNode.gain.setValueAtTime(0.4, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0, audioCtx.currentTime + 0.15);
          gainNode.gain.setValueAtTime(0.4, audioCtx.currentTime + 0.2);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.4);
          break;

        case 'error':
          // Lower descending tone
          oscillator.frequency.setValueAtTime(440, audioCtx.currentTime); // A4
          oscillator.frequency.setValueAtTime(349.23, audioCtx.currentTime + 0.15); // F4
          gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.3);
          break;

        case 'info':
        default:
          // Simple soft tone
          oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
          gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.2);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.2);
          break;
      }
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  }

  /**
   * Close all notifications with a specific tag
   */
  async closeNotification(tag) {
    if (this.serviceWorkerRegistration) {
      const notifications = await this.serviceWorkerRegistration.getNotifications({ tag });
      notifications.forEach(notification => notification.close());
    }
  }

  /**
   * Close all notifications
   */
  async closeAllNotifications() {
    if (this.serviceWorkerRegistration) {
      const notifications = await this.serviceWorkerRegistration.getNotifications();
      notifications.forEach(notification => notification.close());
    }
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
export { NOTIFICATION_TYPES };
